// Step2Academics.jsx
// Step 2: Academics

import React, { useState, useEffect, useRef } from 'react';
import { GraduationCap, Target, Search } from 'lucide-react';
import SelectField from '../../components/ui/SelectField';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Step2Academics = ({ formData, updateField, errors = {} }) => {
  // Generate years 2000-2030
  const gradYears = [];
  for (let year = 2000; year <= 2030; year++) {
    gradYears.push(year.toString());
  }

  const studentTypeOptions = [
    'High School Student',
    'Transfer Student',
    'Adult Learner',
    'College Student',
    'Graduate Candidate'
  ];

  const gpaOptions = ['4.0', '3.75', '3.5', '3.25', '3.0', '2.75', '2.5', '2.0'];

  const rotcOptions = ['Yes', 'No'];

  const _educationFormatOptions = [
    'On Campus',
    'Online',
    'Hybrid',
    'HyFlex',
    'Learn Anywhere'
  ];

  const _majorOptions = [
    'Accounting',
    'Anthropology',
    'Architecture',
    'Art History',
    'Biology',
    'Business Administration',
    'Chemistry',
    'Civil Engineering',
    'Communications',
    'Computer Science',
    'Criminal Justice',
    'Economics',
    'Electrical Engineering',
    'English',
    'Environmental Science',
    'Finance',
    'Graphic Design',
    'History',
    'Information Technology',
    'International Relations',
    'Journalism',
    'Marketing',
    'Mathematics',
    'Mechanical Engineering',
    'Music',
    'Nursing',
    'Philosophy',
    'Physics',
    'Political Science',
    'Psychology',
    'Public Health',
    'Sociology',
    'Theater',
    'Undecided'
  ];

  return (
    <div>
      <h1 className="text-3xl font-bold mb-2" style={{ color: '#2A2F35' }}>
        Academics
      </h1>
      <p className="text-base mb-6" style={{ color: '#6B7280' }}>
        Tell us about your educational background
      </p>

      <div className="space-y-4">
        {/* High School Autocomplete Picker */}
        <HighSchoolPicker 
          formData={formData} 
          updateField={updateField}
          error={errors.highSchool}
        />

        {/* ROTC */}
        <SelectField
          label="ROTC"
          value={formData.rotc}
          onChange={(value) => updateField('rotc', value)}
          options={rotcOptions}
          placeholder="Select"
        />

        {/* Student Type */}
        <SelectField
          label="Student Type"
          value={formData.student_type}
          onChange={(value) => updateField('student_type', value)}
          options={studentTypeOptions}
          icon={<GraduationCap size={20} />}
          placeholder="Select student type"
          required
        />

        {/* High School Graduation Year */}
        <SelectField
          label="High School Graduation Year"
          value={formData.high_school_grad_year}
          onChange={(value) => updateField('high_school_grad_year', value)}
          options={gradYears}
          placeholder="Select year"
          required
        />

        {/* GPA */}
        <SelectField
          label="GPA"
          value={formData.gpa}
          onChange={(value) => updateField('gpa', value)}
          options={gpaOptions}
          icon={<Target size={20} />}
          placeholder="Select GPA"
          required
        />

        {/* SAT Score */}
        <InputField
          label="SAT Score"
          type="number"
          value={formData.sat_score}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '' || (parseInt(value) >= 400 && parseInt(value) <= 1600)) {
              updateField('sat_score', value);
            }
          }}
          placeholder="400-1600"
          min="400"
          max="1600"
        />

        {/* ACT Score */}
        <InputField
          label="ACT Score"
          type="number"
          value={formData.act_score}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 36)) {
              updateField('act_score', value);
            }
          }}
          placeholder="1-36"
          min="1"
          max="36"
        />
      </div>
    </div>
  );
};

// High School Autocomplete Picker Component
const HighSchoolPicker = ({ formData, updateField, error }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimer = useRef(null);

  // Get display value
  const displayValue = formData.highSchool?.name 
    ? `${formData.highSchool.name} — ${formData.highSchool.city}, ${formData.highSchool.state}`
    : query;

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          inputRef.current && !inputRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch high schools with debounce
  const fetchHighSchools = async (searchQuery) => {
    if (searchQuery.length < 3) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/high-schools?query=${encodeURIComponent(searchQuery)}&limit=20`);
      const data = await response.json();
      setResults(data.results || []);
      setIsOpen(data.results?.length > 0);
    } catch (error) {
      console.error('Error fetching high schools:', error);
      setResults([]);
      setIsOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    // Clear selection if user starts typing
    if (formData.highSchool?.id) {
      updateField('highSchool', null);
    }

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer
    debounceTimer.current = setTimeout(() => {
      fetchHighSchools(value);
    }, 300);
  };

  // Handle school selection
  const handleSelect = (school) => {
    setQuery('');
    setIsOpen(false);
    setResults([]);
    // Update compound object
    updateField('highSchool', school);
    // TASK 2 — Also populate individual flat fields so they save to DB
    updateField('high_school_name', school.name || '');
    updateField('high_school_id', school.id || null);
    updateField('high_school_city', school.city || null);
    updateField('high_school_state', school.state || null);
  };

  // Clear selection
  const handleClear = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    updateField('highSchool', null);
    // Clear the flat fields too
    updateField('high_school_name', '');
    updateField('high_school_id', null);
    updateField('high_school_city', null);
    updateField('high_school_state', null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium mb-2" style={{ color: '#2A2F35' }}>
        High School <span style={{ color: '#D92D20' }}>*</span>
      </label>
      
      <div className="relative" ref={inputRef}>
        <div className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: '#6B7280' }}>
          <Search size={20} />
        </div>
        
        <input
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={() => {
            if (query.length >= 3 && results.length > 0) {
              setIsOpen(true);
            }
          }}
          placeholder="Type at least 3 letters to find your high school"
          className="w-full text-sm transition-all pl-12 pr-4"
          style={{
            height: '48px',
            border: error && !formData.highSchool?.id ? '2px solid #D92D20' : '1px solid #E1E4E8',
            borderRadius: '12px',
            color: '#2A2F35',
            backgroundColor: 'white',
            outline: 'none',
          }}
          onFocus={(e) => {
            if (!error || formData.highSchool?.id) {
              e.target.style.border = '2px solid #004C3F';
            }
          }}
          onBlur={(e) => {
            setTimeout(() => {
              if (!error || formData.highSchool?.id) {
                e.target.style.border = '1px solid #E1E4E8';
              }
            }, 200);
          }}
        />

        {formData.highSchool?.id && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            style={{ fontSize: '20px' }}
          >
            ×
          </button>
        )}

        {/* Dropdown Results */}
        {isOpen && results.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute w-full mt-2 bg-white border rounded-xl shadow-lg overflow-hidden z-50"
            style={{
              border: '1px solid #E1E4E8',
              maxHeight: '300px',
              overflowY: 'auto'
            }}
          >
            {results.map((school) => (
              <button
                key={school.id}
                type="button"
                onClick={() => handleSelect(school)}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                style={{ color: '#2A2F35' }}
              >
                <div className="font-medium text-sm">{school.name}</div>
                <div className="text-xs mt-1" style={{ color: '#6B7280' }}>
                  {school.city}, {school.state} • {school.type}
                </div>
              </button>
            ))}
          </div>
        )}

        {isLoading && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            <div className="animate-spin h-4 w-4 border-2 border-[#004C3F] border-t-transparent rounded-full"></div>
          </div>
        )}
      </div>

      {/* Validation Error */}
      {error && !formData.highSchool?.id && (
        <p className="text-xs mt-1" style={{ color: '#D92D20' }}>
          {error}
        </p>
      )}

      {/* Selected School Display */}
      {formData.highSchool?.id && (
        <div className="mt-2 p-3 rounded-lg" style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0' }}>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium" style={{ color: '#15803D' }}>
                {formData.highSchool.name}
              </p>
              <p className="text-xs mt-1" style={{ color: '#16A34A' }}>
                {formData.highSchool.city}, {formData.highSchool.state} {formData.highSchool.zip}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InputField = ({ label, icon, required, ...props }) => (
  <div>
    <label className="block text-sm font-medium mb-2" style={{ color: '#2A2F35' }}>
      {label} {required && <span style={{ color: '#D92D20' }}>*</span>}
    </label>
    <div className="relative">
      {icon && (
        <div className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: '#6B7280' }}>
          {icon}
        </div>
      )}
      <input
        {...props}
        className={`w-full text-sm transition-all ${icon ? 'pl-12' : 'pl-4'} pr-4`}
        style={{
          height: '48px',
          border: '1px solid #E1E4E8',
          borderRadius: '12px',
          color: '#2A2F35',
          backgroundColor: 'white',
          outline: 'none',
        }}
        onFocus={(e) => {
          e.target.style.border = '2px solid #004C3F';
        }}
        onBlur={(e) => {
          e.target.style.border = '1px solid #E1E4E8';
        }}
      />
    </div>
  </div>
);

export default Step2Academics;
